import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

const getAspectRatio = file =>
    new Promise(resolve => {
        const img = new Image()
        img.onload = () => resolve(img.width / img.height)
        img.src = file
    })

const drawUnderlinedField = (
    pdf,
    {
        label,
        value,
        y,
        lineStartX,
        lineEndX,
        labelAlign = 'left',
        valueAlign = 'left',
        minRowHeight = 5,
        lineHeight = 3.5,
        rowGap = 1,
    }
) => {
    const maxWidth = lineEndX - lineStartX - 2

    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    const lines = value ? pdf.splitTextToSize(String(value), maxWidth) : []
    const valueLineCount = Math.max(lines.length, 1)
    const textBlockHeight = valueLineCount * lineHeight
    const rowHeight = Math.max(minRowHeight, textBlockHeight + 2)
    const underlineY = y + rowHeight
    const labelY = y + 3

    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    if (labelAlign === 'right') {
        pdf.text(`${label}:`, lineStartX - 2, labelY, { align: 'right' })
    } else {
        pdf.text(`${label}:`, lineStartX, labelY)
    }

    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    if (lines.length) {
        if (valueAlign === 'center') {
            const centerX = (lineStartX + lineEndX) / 2
            lines.forEach((line, index) => {
                const textY =
                    underlineY - 1.2 - (lines.length - 1 - index) * lineHeight
                pdf.text(line, centerX, textY, { align: 'center' })
            })
        } else {
            lines.forEach((line, index) => {
                const textY =
                    underlineY - 1.2 - (lines.length - 1 - index) * lineHeight
                pdf.text(line, lineStartX + 1, textY)
            })
        }
    }

    pdf.setLineWidth(0.2)
    pdf.line(lineStartX, underlineY, lineEndX, underlineY)

    return underlineY + rowGap
}

const drawMultilineField = (
    pdf,
    { label, value, x, y, width, minHeight, lineHeight = 4, labelGap = 4 }
) => {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text(`${label}:`, x, y)

    const textX = x + 1
    const textY = y + labelGap
    const textWidth = width - 2

    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    const lines = value ? pdf.splitTextToSize(String(value), textWidth) : []
    const contentHeight = Math.max(
        minHeight,
        labelGap + Math.max(lines.length, 1) * lineHeight + 2
    )
    const bottomY = y + contentHeight

    lines.forEach((line, index) => {
        pdf.text(line, textX, textY + index * lineHeight)
    })

    return bottomY
}

const drawSignatureField = (
    pdf,
    { label, x, y, lineStartX, lineEndX, subtitle }
) => {
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text(`${label}:`, x, y)
    pdf.setLineWidth(0.2)
    pdf.line(lineStartX, y + 1.2, lineEndX, y + 1.2)
    if (subtitle) {
        pdf.setFontSize(10)
        pdf.text(subtitle, lineStartX, y + 5.5)
    }
}

export const generateDeliveryTicketPDF = async ({
    logoBase64,
    projectLabel,
    scopeLabel,
    deliveryDate,
    contactPerson,
    note,
    to,
    projectManager,
    items,
}) => {
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter',
    })

    const pageWidth = pdf.internal.pageSize.width
    const margin = 15
    const headerTop = 14
    const rightValueWidth = 46
    const rightLineEnd = pageWidth - margin
    const rightLineStart = rightLineEnd - rightValueWidth
    const toFieldWidth = pageWidth / 2 - margin + 8

    let logoHeight = 0
    if (logoBase64) {
        const aspectRatio = await getAspectRatio(logoBase64)
        const maxLogoHeight = 24
        let imgHeight = maxLogoHeight
        let imgWidth = imgHeight * aspectRatio
        const maxLogoWidth = 92
        if (imgWidth > maxLogoWidth) {
            imgWidth = maxLogoWidth
            imgHeight = imgWidth / aspectRatio
        }
        pdf.addImage(logoBase64, 'PNG', margin, headerTop, imgWidth, imgHeight)
        logoHeight = imgHeight
    }

    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'bold')
    pdf.text('DELIVERY TICKET', pageWidth - margin - 20, headerTop + 5, {
        align: 'right',
    })

    const rightFields = [
        { label: 'DELIVERY DATE', value: deliveryDate || '' },
        { label: 'PROJECT', value: projectLabel },
        { label: 'SCOPE', value: scopeLabel },
        { label: 'PROJECT MANAGER', value: projectManager },
        { label: 'CONTACT PERSON', value: contactPerson },
    ]

    const fieldSpacing = 1
    let rightY = headerTop + 12
    rightFields.forEach(field => {
        rightY = drawUnderlinedField(pdf, {
            label: field.label,
            value: field.value,
            y: rightY,
            lineStartX: rightLineStart,
            lineEndX: rightLineEnd,
            labelAlign: 'right',
            valueAlign: 'center',
            rowGap: fieldSpacing,
        })
    })

    const toY = headerTop + logoHeight + 4
    const toBottomY = drawMultilineField(pdf, {
        label: 'TO',
        value: to,
        x: margin,
        y: toY,
        width: toFieldWidth,
        minHeight: 18,
    })

    let y = Math.max(toBottomY + 4, rightY + 2)

    const tableRows = (items || []).map(row => [
        '',
        row.itemCode || '',
        row.description || '',
        row.finishAndUCCode || '',
        String(row.qty ?? ''),
    ])
    const minTableRows = 12
    while (tableRows.length < minTableRows) {
        tableRows.push(['', '', '', '', ''])
    }

    const tableWidth = pageWidth - margin * 2
    pdf.autoTable({
        startY: y,
        head: [['ELEVATION', 'ITEM', 'DESCRIPTION', 'MATERIAL/COLOR', 'QTY']],
        body: tableRows,
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2,
            valign: 'middle',
            lineWidth: 0.2,
            lineColor: [0, 0, 0],
        },
        headStyles: {
            fillColor: [200, 200, 200],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle',
            lineWidth: 0.2,
            lineColor: [0, 0, 0],
        },
        columnStyles: {
            0: { cellWidth: tableWidth * 0.15, halign: 'center' },
            1: { cellWidth: tableWidth * 0.12, halign: 'center' },
            2: { cellWidth: tableWidth * 0.35 },
            3: { cellWidth: tableWidth * 0.28 },
            4: { cellWidth: tableWidth * 0.1, halign: 'center' },
        },
        margin: { left: margin, right: margin },
        tableLineWidth: 0.2,
        tableLineColor: [0, 0, 0],
    })

    y = pdf.lastAutoTable.finalY + 8
    const noteBottomY = drawMultilineField(pdf, {
        label: 'NOTE',
        value: note,
        x: margin,
        y,
        width: pageWidth - margin * 2,
        minHeight: 24,
    })

    y = noteBottomY + 10
    const signedBlockX = pageWidth * 0.55
    const copyToLineStart = margin + 20
    const copyToLineEnd = signedBlockX - 8
    const signedLineStart = signedBlockX + 16
    const signedLineEnd = pageWidth - margin

    drawSignatureField(pdf, {
        label: 'COPY TO',
        x: margin,
        y,
        lineStartX: copyToLineStart,
        lineEndX: copyToLineEnd,
    })
    drawSignatureField(pdf, {
        label: 'SIGNED',
        x: signedBlockX,
        y,
        lineStartX: signedLineStart,
        lineEndX: signedLineEnd,
        subtitle: 'Your Name, Title',
    })

    y += 12
    pdf.setFont('helvetica', 'bolditalic')
    pdf.setFontSize(8)
    const ackText =
        'The undersigned individual hereby acknowledges the receipt and the delivered goods detailed on the included transmittal above. Furthermore, this document acknowledges that delivered goods underwent proper inspection and without defect. By signing this document you are assuming all financial responsibilities in the event that the items are misplaced or lost.'
    const ackLines = pdf.splitTextToSize(ackText, pageWidth - margin * 2)
    pdf.text(ackLines, margin, y)

    return pdf.output('blob')
}

export const downloadDeliveryTicketPDF = async (params, filename) => {
    const blob = await generateDeliveryTicketPDF(params)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}
